import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import {
  type ComponentProps,
  createContext,
  type FC,
  type HTMLAttributes,
  type MouseEventHandler,
  type ReactNode,
  useContext,
  useId,
} from "react";

import { cn } from "../utils";
import { Button } from "./button";
import { Icon, type MaterialSymbol } from "./icon";
import { Separator } from "./separator";
import { Tooltip, TooltipContent, TooltipPortal, TooltipTrigger } from "./tooltip";

export type ToolbarPosition =
  | "top"
  | "bottom"
  | "left"
  | "right"
  | "top-left"
  | "left-top"
  | "top-right"
  | "right-top"
  | "bottom-left"
  | "left-bottom"
  | "bottom-right"
  | "right-bottom";

export type ToolbarOrientation = "horizontal" | "vertical";

export type ToolbarPopoverSide = "top" | "right" | "bottom" | "left";

export interface ToolbarContextValue {
  position?: ToolbarPosition;
  orientation?: ToolbarOrientation;
  popoverSide?: ToolbarPopoverSide;
}

const toolbarVariants = cva("z-5 flex gap-2", {
  variants: {
    position: {
      top: "top-4 left-1/2 -translate-x-1/2",
      bottom: "bottom-4 left-1/2 -translate-x-1/2",
      left: "left-4 top-1/2 -translate-y-1/2",
      right: "right-4 top-1/2 -translate-y-1/2",
      "left-top": "top-4 left-4",
      "top-left": "top-4 left-4",
      "top-right": "top-4 right-4",
      "right-top": "top-4 right-4",
      "bottom-left": "bottom-4 left-4",
      "left-bottom": "bottom-4 left-4",
      "bottom-right": "bottom-4 right-4",
      "right-bottom": "bottom-4 right-4",
    },
    orientation: {
      horizontal: "flex-row",
      vertical: "flex-col",
    },
  },
});

export const ToolbarContext = createContext<ToolbarContextValue>({} as ToolbarContextValue);

export interface ToolbarProps extends HTMLAttributes<HTMLDivElement>, VariantProps<typeof toolbarVariants> {
  asChild?: boolean;
  orientation?: ToolbarContextValue["orientation"];
  popoverSide?: ToolbarContextValue["popoverSide"];
}

export const Toolbar: FC<ToolbarProps> = ({
  position,
  asChild,
  className,
  popoverSide: propsPopoverSide,
  ...props
}) => {
  const Comp = asChild ? Slot : "div";
  let orientation: ToolbarContextValue["orientation"];
  let popoverSide: ToolbarContextValue["popoverSide"];

  const side: ToolbarPopoverSide = position?.split("-")[0] as ToolbarPopoverSide;

  if (position?.startsWith("top") || position?.startsWith("bottom")) {
    orientation = "horizontal";
  } else if (position?.startsWith("left") || position?.startsWith("right")) {
    orientation = "vertical";
  }

  switch (side) {
    case "top":
      popoverSide = "bottom";
      break;
    case "right":
      popoverSide = "left";
      break;
    case "bottom":
      popoverSide = "top";
      break;
    case "left":
      popoverSide = "right";
      break;
  }

  return (
    <ToolbarContext.Provider
      {...props}
      value={{
        position: position!,
        orientation: props.orientation || orientation,
        popoverSide: propsPopoverSide || popoverSide,
      }}
    >
      <Comp
        className={cn(toolbarVariants({ position, orientation: props.orientation || orientation, className }), {
          absolute: !!position,
        })}
        {...props}
      />
    </ToolbarContext.Provider>
  );
};

const useToolbar = () => {
  const context = useContext(ToolbarContext);

  if (!context) {
    throw new Error("useToolbar must be used within a ToolbarProvider");
  }

  return context;
};

export interface ToolbarMenuProps extends HTMLAttributes<HTMLDivElement> {
  orientation?: "horizontal" | "vertical";
}

export const ToolbarMenu: FC<ToolbarMenuProps> = ({ className, ...props }) => {
  const { orientation } = useToolbar();

  return (
    <div
      className={cn(
        "flex items-center outline outline-background-contrast rounded bg-popover shadow-sm",
        {
          "flex-row px-0.5": props.orientation === "horizontal" || orientation === "horizontal",
          "flex-col py-0.5": props.orientation === "vertical" || orientation === "vertical",
        },
        className
      )}
      {...props}
    />
  );
};

export interface ToolbarMenuItemProps extends Omit<HTMLAttributes<HTMLDivElement>, "onClick"> {
  icon: MaterialSymbol;
  label?: ReactNode;
  buttonText?: ReactNode;
  shortcut?: string;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  buttonProps?: ComponentProps<typeof Button>;
  active?: boolean;
  disabled?: boolean;
  tooltipOpen?: boolean;
}

export const ToolbarMenuItem = ({
  className,
  icon,
  label,
  buttonText,
  shortcut,
  onClick,
  buttonProps,
  disabled,
  active,
  tooltipOpen,
  ...props
}: React.ComponentPropsWithRef<"button"> & ToolbarMenuItemProps) => {
  const id = useId();
  const { popoverSide, orientation } = useToolbar();

  return (
    <div
      className={cn("z-10", {
        "py-0.5": orientation === "horizontal",
        "px-0.5": orientation === "vertical",
      })}
    >
      <Tooltip delayDuration={0} open={tooltipOpen}>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            display={buttonText ? "default" : "icon"}
            size={buttonText ? "sm" : "default"}
            aria-labelledby={id}
            onClick={onClick}
            disabled={disabled}
            className={cn("h-8 rounded-sm", {
              "w-8": !buttonText,
              "gap-2 pl-2 pr-3": !!buttonText,
              "bg-primary text-primary-foreground": active,
            })}
            {...props}
          >
            <span className="inline-flex">
              <Icon icon={icon} />
            </span>
            {buttonText && <span>{buttonText}</span>}
          </Button>
        </TooltipTrigger>
        <TooltipPortal>
          <TooltipContent id={id} className="flex items-center bg-foreground! text-background!" side={popoverSide}>
            {label}
            {shortcut && (
              <span className="inline-flex items-center gap-0.5 ml-2 -mr-1.5">
                {[...shortcut].map((k, i) => (
                  <kbd
                    key={i}
                    className="inline-block text-xs text-muted border border-muted-foreground px-1 py-0.5 rounded-sm leading-none"
                  >
                    {k}
                  </kbd>
                ))}
              </span>
            )}
          </TooltipContent>
        </TooltipPortal>
      </Tooltip>
    </div>
  );
};

ToolbarMenuItem.displayName = "ToolbarMenuItem";

export interface ToolbarSeparatorProps extends HTMLAttributes<HTMLDivElement> {}

export const ToolbarSeparator: FC<ToolbarSeparatorProps> = ({ className, ...props }) => {
  const { orientation } = useToolbar();

  const separatorOrientation = orientation === "horizontal" ? "vertical" : "horizontal";

  return (
    <div
      className={cn(
        "flex items-center",
        {
          "h-full mx-0.5": separatorOrientation === "vertical",
          "w-full my-0.5": separatorOrientation === "horizontal",
        },
        className
      )}
      {...props}
    >
      <Separator orientation={separatorOrientation} />
    </div>
  );
};
